/**
 * A row of single-digit boxes for TOTP-style codes — MFA enrolment
 * confirmation, MFA disable, and the login-time MFA challenge all share this.
 *
 * Behaviour: typing a digit advances focus to the next box; Backspace on an
 * empty box moves focus back and clears the previous box; pasting a string of
 * digits (e.g. from a password manager or SMS) fills every box from the paste
 * point; `onComplete` fires exactly once per completed code, when the last box
 * fills — the call site decides what "complete" means (auto-submit).
 */
import { useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  /** Fires once when `value` reaches `length` digits, with that value. */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Aria label prefix per box, e.g. "Verification code digit". */
  label?: string;
  error?: boolean;
}

export function CodeInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
  label = "Verification code digit",
  error,
}: CodeInputProps) {
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Guards against firing onComplete more than once for the same completed
  // value (e.g. re-renders after the value is already full).
  const firedFor = useRef<string | null>(null);

  useEffect(() => {
    if (autoFocus) inputRefs.current[0]?.focus();
    // Only on mount — re-focusing on every value change would steal focus
    // from wherever the user just navigated to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value.length === length && firedFor.current !== value) {
      firedFor.current = value;
      onComplete?.(value);
    }
    if (value.length < length) {
      firedFor.current = null;
    }
  }, [value, length, onComplete]);

  const setDigitAt = useCallback(
    (index: number, digit: string) => {
      const next = digits.slice();
      next[index] = digit;
      onChange(next.join("").slice(0, length));
    },
    [digits, length, onChange],
  );

  function handleChange(index: number, raw: string) {
    // Only the last character typed matters — browsers report the WHOLE
    // field value on change, including whatever was already there.
    const incoming = raw.replace(/\D/g, "");
    if (!incoming) {
      setDigitAt(index, "");
      return;
    }
    const digit = incoming[incoming.length - 1];
    setDigitAt(index, digit);
    if (index < length - 1) inputRefs.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      setDigitAt(index - 1, "");
      inputRefs.current[index - 1]?.focus();
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
      return;
    }
    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted.slice(0, length));
    const lastFilled = Math.min(pasted.length, length) - 1;
    if (lastFilled >= 0) inputRefs.current[Math.min(lastFilled, length - 1)]?.focus();
  }

  return (
    <div className="flex justify-center gap-2" role="group" aria-label={label}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          pattern="\d*"
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`${label} ${index + 1} of ${length}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            "h-12 w-10 rounded-[9px] border bg-[var(--cp-surface)] text-center text-lg font-semibold text-[var(--cp-ink)] transition-colors",
            "focus:outline-none focus:ring-[3px] focus:ring-[var(--cp-accent-wash)]",
            error
              ? "border-[var(--cp-rose-ink)]"
              : "border-[var(--cp-line)] focus:border-[var(--cp-accent)]",
            disabled && "cursor-not-allowed bg-[var(--cp-surface-2)] opacity-60",
          )}
        />
      ))}
    </div>
  );
}
